import type { SampleAssetMeta, SampleFolderMeta, SamplerRuntimeSample } from "../../../../src/lib/audio";
import {
  getCachedSample,
  getFactoryCycleSample,
  isFactorySampleId,
  listFactoryCycleMetas,
  putCachedSample,
  removeCachedSample,
} from "../../../../src/lib/audio";

const INDEX_PATH = "sample-index.json";
const SAMPLES_DIR = "samples";
const LIBRARY_VERSION = 2 as const;

type SampleIndex = Record<string, SampleAssetMeta>;
type FolderIndex = Record<string, SampleFolderMeta>;

type SampleLibraryDocumentV2 = {
  version: typeof LIBRARY_VERSION;
  folders: FolderIndex;
  samples: SampleIndex;
};

export type DeletedSamplePayload = {
  meta: SampleAssetMeta;
  pcm: Float32Array;
};

/** Cascade delete of a folder subtree (for undo). */
export type DeletedFolderPayload = {
  folders: SampleFolderMeta[];
  samples: DeletedSamplePayload[];
};

let memoryIndex: SampleIndex = {};
let memoryFolders: FolderIndex = {};
const memoryPcm = new Map<string, Float32Array>();

type CatalogListener = () => void;
const catalogListeners = new Set<CatalogListener>();

/** Subscribe to sample catalog mutations (ingest / rename / delete / restore / folders). */
export function subscribeSampleCatalog(listener: CatalogListener): () => void {
  catalogListeners.add(listener);
  return () => {
    catalogListeners.delete(listener);
  };
}

export function notifySampleCatalogChanged(): void {
  for (const listener of catalogListeners) {
    try {
      listener();
    } catch {
      // ignore subscriber errors
    }
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function newSampleId(): string {
  return newId("smp");
}

function newFolderId(): string {
  return newId("fld");
}

function normalizeFolderId(folderId: string | null | undefined): string | null {
  if (folderId == null || folderId === "") {
    return null;
  }
  return folderId;
}

function asDocument(raw: unknown): SampleLibraryDocumentV2 {
  if (!raw || typeof raw !== "object") {
    return { version: LIBRARY_VERSION, folders: {}, samples: {} };
  }
  const obj = raw as Record<string, unknown>;
  if (obj.version === LIBRARY_VERSION && obj.samples && typeof obj.samples === "object") {
    const folders =
      obj.folders && typeof obj.folders === "object"
        ? (obj.folders as FolderIndex)
        : {};
    return {
      version: LIBRARY_VERSION,
      folders: { ...folders },
      samples: { ...(obj.samples as SampleIndex) },
    };
  }
  // Legacy v1: bare id → SampleAssetMeta map.
  return {
    version: LIBRARY_VERSION,
    folders: {},
    samples: { ...(obj as SampleIndex) },
  };
}

function snapshotDocument(): SampleLibraryDocumentV2 {
  return {
    version: LIBRARY_VERSION,
    folders: { ...memoryFolders },
    samples: { ...memoryIndex },
  };
}

async function opfsRoot(): Promise<FileSystemDirectoryHandle | null> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.getDirectory) {
      return null;
    }
    const root = await navigator.storage.getDirectory();
    const adam = await root.getDirectoryHandle("adam", { create: true });
    return adam;
  } catch {
    return null;
  }
}

async function samplesDir(): Promise<FileSystemDirectoryHandle | null> {
  const root = await opfsRoot();
  if (!root) {
    return null;
  }
  return root.getDirectoryHandle(SAMPLES_DIR, { create: true });
}

/** Once hydrated, memory is source of truth — avoid OPFS read races after sync writes. */
let libraryHydrated = false;
let hydratePromise: Promise<void> | null = null;

async function hydrateLibraryFromOpfs(): Promise<void> {
  const root = await opfsRoot();
  if (!root) {
    libraryHydrated = true;
    return;
  }
  try {
    const handle = await root.getFileHandle(INDEX_PATH);
    const file = await handle.getFile();
    const text = await file.text();
    const doc = asDocument(JSON.parse(text));
    memoryIndex = { ...doc.samples };
    memoryFolders = { ...doc.folders };
  } catch {
    // empty / missing index — keep memory
  }
  libraryHydrated = true;
}

async function readLibrary(): Promise<SampleLibraryDocumentV2> {
  if (!libraryHydrated) {
    if (!hydratePromise) {
      hydratePromise = hydrateLibraryFromOpfs().finally(() => {
        hydratePromise = null;
      });
    }
    await hydratePromise;
  }
  return snapshotDocument();
}

/** Await until OPFS catalog is loaded into memory (safe before sync mutations). */
export async function ensureSampleLibraryReady(): Promise<void> {
  await readLibrary();
}

/** @deprecated Prefer readLibrary — kept for call sites that only need samples. */
async function readIndex(): Promise<SampleIndex> {
  const doc = await readLibrary();
  return { ...doc.samples };
}

async function writeLibrary(doc: SampleLibraryDocumentV2): Promise<void> {
  memoryIndex = { ...doc.samples };
  memoryFolders = { ...doc.folders };
  const root = await opfsRoot();
  if (!root) {
    return;
  }
  const handle = await root.getFileHandle(INDEX_PATH, { create: true });
  const writable = await handle.createWritable();
  await writable.write(`${JSON.stringify({
    version: LIBRARY_VERSION,
    folders: memoryFolders,
    samples: memoryIndex,
  })}\n`);
  await writable.close();
}

async function writeIndex(index: SampleIndex): Promise<void> {
  await writeLibrary({
    version: LIBRARY_VERSION,
    folders: memoryFolders,
    samples: index,
  });
}

function persistLibrary(): void {
  void writeLibrary(snapshotDocument());
}

async function writePcm(id: string, pcm: Float32Array): Promise<void> {
  memoryPcm.set(id, pcm);
  putCachedSample({
    id,
    name: memoryIndex[id]?.name ?? id,
    sampleRate: memoryIndex[id]?.sampleRate ?? 48_000,
    pcm,
    rootKey: memoryIndex[id]?.rootKey,
    oneCycle: memoryIndex[id]?.oneCycle,
  });
  const dir = await samplesDir();
  if (!dir) {
    return;
  }
  const sampleDir = await dir.getDirectoryHandle(id, { create: true });
  const handle = await sampleDir.getFileHandle("audio.bin", { create: true });
  const writable = await handle.createWritable();
  const copy = pcm instanceof Float32Array && pcm.byteOffset === 0 && pcm.byteLength === pcm.buffer.byteLength
    ? pcm
    : (() => {
        const next = new Float32Array(pcm.length);
        next.set(pcm);
        return next;
      })();
  await writable.write(copy.buffer);
  await writable.close();
  const metaHandle = await sampleDir.getFileHandle("meta.json", { create: true });
  const metaWritable = await metaHandle.createWritable();
  await metaWritable.write(`${JSON.stringify(memoryIndex[id] ?? { id })}\n`);
  await metaWritable.close();
}

async function readPcm(id: string, lengthFrames: number): Promise<Float32Array | undefined> {
  const mem = memoryPcm.get(id);
  if (mem) {
    return mem;
  }
  const cached = getCachedSample(id);
  if (cached) {
    return cached.pcm;
  }
  const dir = await samplesDir();
  if (!dir) {
    return undefined;
  }
  try {
    const sampleDir = await dir.getDirectoryHandle(id);
    const handle = await sampleDir.getFileHandle("audio.bin");
    const file = await handle.getFile();
    const buf = await file.arrayBuffer();
    const frames = lengthFrames > 0 ? lengthFrames : Math.floor(buf.byteLength / 4);
    const pcm = new Float32Array(buf, 0, frames);
    memoryPcm.set(id, pcm);
    return pcm;
  } catch {
    return undefined;
  }
}

function nextSortKey(parentId: string | null, kind: "folder" | "sample"): number {
  let max = -1;
  if (kind === "folder") {
    for (const folder of Object.values(memoryFolders)) {
      if (normalizeFolderId(folder.parentId) === parentId) {
        max = Math.max(max, folder.sortKey);
      }
    }
  } else {
    for (const sample of Object.values(memoryIndex)) {
      if (normalizeFolderId(sample.folderId) === parentId) {
        max = Math.max(max, sample.sortKey ?? 0);
      }
    }
  }
  return max + 1;
}

function assertFolderExists(folderId: string | null): void {
  if (folderId == null) {
    return;
  }
  if (!memoryFolders[folderId]) {
    throw new Error("Folder not found");
  }
}

/** True if `maybeAncestorId` is `folderId` or an ancestor of it. */
export function isFolderAncestor(
  folderId: string,
  maybeAncestorId: string,
  folders: FolderIndex = memoryFolders,
): boolean {
  let cursor: string | null = folderId;
  const seen = new Set<string>();
  while (cursor) {
    if (cursor === maybeAncestorId) {
      return true;
    }
    if (seen.has(cursor)) {
      return false;
    }
    seen.add(cursor);
    cursor = normalizeFolderId(folders[cursor]?.parentId);
  }
  return false;
}

/** Collect folder id + all descendant folder ids. */
export function collectFolderSubtreeIds(
  folderId: string,
  folders: FolderIndex = memoryFolders,
): string[] {
  const out: string[] = [];
  const queue = [folderId];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id) || !folders[id]) {
      continue;
    }
    seen.add(id);
    out.push(id);
    for (const child of Object.values(folders)) {
      if (normalizeFolderId(child.parentId) === id) {
        queue.push(child.id);
      }
    }
  }
  return out;
}

function compareNameId(a: { name: string; id: string }, b: { name: string; id: string }): number {
  return a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

function compareSortThenName(
  a: { name: string; id: string; sortKey?: number },
  b: { name: string; id: string; sortKey?: number },
): number {
  const sa = a.sortKey ?? 0;
  const sb = b.sortKey ?? 0;
  if (sa !== sb) {
    return sa - sb;
  }
  return compareNameId(a, b);
}

/** List catalog metas (factory + imported). */
export async function listSampleAssets(): Promise<SampleAssetMeta[]> {
  const index = await readIndex();
  const factory = listFactoryCycleMetas().map(f => ({
    id: f.id,
    name: f.name,
    sampleRate: f.sampleRate,
    channels: 1 as const,
    lengthFrames: f.lengthFrames,
    rootKey: f.rootKey,
    source: f.source,
    oneCycle: f.oneCycle,
    createdAt: "1970-01-01T00:00:00.000Z",
  }));
  return [...factory, ...Object.values(index)];
}

/** Resolve a sample id to runtime PCM (factory or OPFS/memory). */
export async function resolveSampleRuntime(id: string): Promise<SamplerRuntimeSample | undefined> {
  const factory = getFactoryCycleSample(id);
  if (factory) {
    return factory;
  }
  const cached = getCachedSample(id);
  if (cached) {
    return cached;
  }
  const index = await readIndex();
  const meta = index[id];
  if (!meta) {
    return undefined;
  }
  const pcm = await readPcm(id, meta.lengthFrames);
  if (!pcm) {
    return undefined;
  }
  const sample: SamplerRuntimeSample = {
    id,
    name: meta.name,
    sampleRate: meta.sampleRate,
    pcm,
    rootKey: meta.rootKey,
    ...(meta.oneCycle ? { oneCycle: true } : {}),
  };
  putCachedSample(sample);
  return sample;
}

/** Ingest decoded mono PCM into OPFS + host cache. */
export async function ingestSamplePcm(options: {
  name: string;
  pcm: Float32Array;
  sampleRate: number;
  rootKey?: number;
  source?: SampleAssetMeta["source"];
  id?: string;
  folderId?: string | null;
  oneCycle?: boolean;
  createdAt?: string;
  sortKey?: number;
}): Promise<SampleAssetMeta> {
  const id = options.id ?? newSampleId();
  if (isFactorySampleId(id)) {
    throw new Error("Cannot overwrite a factory sample id");
  }
  await readLibrary();
  const folderId = normalizeFolderId(options.folderId);
  assertFolderExists(folderId);
  const meta: SampleAssetMeta = {
    id,
    name: options.name.trim() || "Sample",
    sampleRate: options.sampleRate,
    channels: 1,
    lengthFrames: options.pcm.length,
    rootKey: options.rootKey ?? 60,
    source: options.source ?? "import",
    createdAt: options.createdAt ?? nowIso(),
    ...(options.oneCycle ? { oneCycle: true } : {}),
    ...(folderId ? { folderId } : {}),
    sortKey: options.sortKey ?? nextSortKey(folderId, "sample"),
  };
  memoryIndex = { ...memoryIndex, [id]: meta };
  persistLibrary();
  await writePcm(id, options.pcm);
  notifySampleCatalogChanged();
  return meta;
}

/** Rehydrate OPFS samples listed in a package manifest. */
export async function ingestSampleFromPackage(
  meta: SampleAssetMeta,
  pcm: Float32Array,
): Promise<SampleAssetMeta> {
  return ingestSamplePcm({
    id: meta.id,
    name: meta.name,
    pcm,
    sampleRate: meta.sampleRate,
    rootKey: meta.rootKey,
    source: meta.source ?? "import",
    folderId: meta.folderId,
    oneCycle: meta.oneCycle,
    createdAt: meta.createdAt,
    sortKey: meta.sortKey,
  });
}

/** Collect PCM + meta for export (skips factory ids — regenerated on load). */
export async function collectSamplesForExport(
  sampleIds: string[],
): Promise<Array<{ meta: SampleAssetMeta; pcm: Float32Array }>> {
  const out: Array<{ meta: SampleAssetMeta; pcm: Float32Array }> = [];
  const index = await readIndex();
  for (const id of sampleIds) {
    if (isFactorySampleId(id)) {
      continue;
    }
    const runtime = await resolveSampleRuntime(id);
    const meta = index[id] ?? (runtime
      ? {
          id,
          name: runtime.name,
          sampleRate: runtime.sampleRate,
          channels: 1 as const,
          lengthFrames: runtime.pcm.length,
          rootKey: runtime.rootKey,
          source: "import" as const,
          createdAt: nowIso(),
        }
      : null);
    if (!runtime || !meta) {
      continue;
    }
    out.push({ meta, pcm: runtime.pcm });
  }
  return out;
}

/**
 * Folders needed so exported sample `folderId`s remain meaningful.
 * Walks ancestors of each sample's folder.
 */
export async function collectFoldersForExport(
  sampleMetas: SampleAssetMeta[],
): Promise<SampleFolderMeta[]> {
  await readLibrary();
  const needed = new Set<string>();
  for (const meta of sampleMetas) {
    let cursor = normalizeFolderId(meta.folderId);
    while (cursor) {
      if (needed.has(cursor)) {
        break;
      }
      if (!memoryFolders[cursor]) {
        break;
      }
      needed.add(cursor);
      cursor = normalizeFolderId(memoryFolders[cursor]!.parentId);
    }
  }
  return [...needed]
    .map(id => memoryFolders[id]!)
    .filter(Boolean)
    .sort(compareSortThenName);
}

/** Merge package folders into the catalog (id-preserving; skips collisions with different parents). */
export async function ingestFoldersFromPackage(folders: SampleFolderMeta[]): Promise<void> {
  if (folders.length === 0) {
    return;
  }
  await readLibrary();
  const next = { ...memoryFolders };
  // Insert parents before children by repeating a few topological passes.
  const pending = [...folders];
  let guard = pending.length + 2;
  while (pending.length > 0 && guard-- > 0) {
    const still: SampleFolderMeta[] = [];
    for (const folder of pending) {
      const parent = normalizeFolderId(folder.parentId);
      if (parent && !next[parent] && folders.some(f => f.id === parent)) {
        still.push(folder);
        continue;
      }
      const parentOk = parent == null || Boolean(next[parent]);
      if (!parentOk) {
        still.push({ ...folder, parentId: null });
        continue;
      }
      if (!next[folder.id]) {
        next[folder.id] = {
          id: folder.id,
          name: folder.name.trim() || "Folder",
          parentId: parent,
          sortKey: folder.sortKey ?? nextSortKey(parent, "folder"),
          createdAt: folder.createdAt || nowIso(),
        };
      }
    }
    if (still.length === pending.length) {
      for (const folder of still) {
        if (!next[folder.id]) {
          next[folder.id] = {
            ...folder,
            parentId: null,
            name: folder.name.trim() || "Folder",
            sortKey: folder.sortKey ?? 0,
            createdAt: folder.createdAt || nowIso(),
          };
        }
      }
      break;
    }
    pending.splice(0, pending.length, ...still);
  }
  memoryFolders = next;
  persistLibrary();
  notifySampleCatalogChanged();
}

/** List imported (non-factory) samples only. */
export async function listImportedSampleAssets(): Promise<SampleAssetMeta[]> {
  const all = await listSampleAssets();
  return all
    .filter(a => !isFactorySampleId(a.id) && a.source !== "factory")
    .sort(compareSortThenName);
}

export async function listSampleFolders(): Promise<SampleFolderMeta[]> {
  await readLibrary();
  return Object.values(memoryFolders).sort(compareSortThenName);
}

export async function getSampleAssetMeta(id: string): Promise<SampleAssetMeta | undefined> {
  if (isFactorySampleId(id)) {
    return undefined;
  }
  const index = await readIndex();
  return index[id];
}

export async function getSampleFolderMeta(id: string): Promise<SampleFolderMeta | undefined> {
  await readLibrary();
  return memoryFolders[id];
}

/** Rename an imported sample (OPFS + cache). Rejects factory ids. */
export async function renameSampleAsset(id: string, nextName: string): Promise<SampleAssetMeta> {
  await readLibrary();
  return renameSampleAssetSync(id, nextName);
}

/** Sync rename (index must already be loaded / warm). */
export function renameSampleAssetSync(id: string, nextName: string): SampleAssetMeta {
  if (isFactorySampleId(id)) {
    throw new Error("Cannot rename a factory sample");
  }
  const name = nextName.trim() || "Sample";
  const meta = memoryIndex[id];
  if (!meta) {
    throw new Error("Sample not found");
  }
  const updated: SampleAssetMeta = { ...meta, name };
  memoryIndex = { ...memoryIndex, [id]: updated };
  const pcm = memoryPcm.get(id) ?? getCachedSample(id)?.pcm;
  if (pcm) {
    memoryPcm.set(id, pcm);
    putCachedSample({
      id,
      name,
      sampleRate: updated.sampleRate,
      pcm,
      rootKey: updated.rootKey,
      oneCycle: updated.oneCycle,
    });
  }
  persistLibrary();
  void (async () => {
    try {
      const dir = await samplesDir();
      const sampleDir = await dir?.getDirectoryHandle(id);
      if (sampleDir) {
        const metaHandle = await sampleDir.getFileHandle("meta.json", { create: true });
        const metaWritable = await metaHandle.createWritable();
        await metaWritable.write(`${JSON.stringify(updated)}\n`);
        await metaWritable.close();
      }
    } catch {
      // ignore
    }
  })();
  notifySampleCatalogChanged();
  return updated;
}

export async function createSampleFolder(options: {
  name: string;
  parentId?: string | null;
}): Promise<SampleFolderMeta> {
  await readLibrary();
  return createSampleFolderSync(options);
}

export function createSampleFolderSync(options: {
  name: string;
  parentId?: string | null;
  id?: string;
  sortKey?: number;
  createdAt?: string;
}): SampleFolderMeta {
  const parentId = normalizeFolderId(options.parentId);
  assertFolderExists(parentId);
  const folder: SampleFolderMeta = {
    id: options.id ?? newFolderId(),
    name: options.name.trim() || "Folder",
    parentId,
    sortKey: options.sortKey ?? nextSortKey(parentId, "folder"),
    createdAt: options.createdAt ?? nowIso(),
  };
  memoryFolders = { ...memoryFolders, [folder.id]: folder };
  persistLibrary();
  notifySampleCatalogChanged();
  return folder;
}

export async function renameSampleFolder(id: string, nextName: string): Promise<SampleFolderMeta> {
  await readLibrary();
  return renameSampleFolderSync(id, nextName);
}

export function renameSampleFolderSync(id: string, nextName: string): SampleFolderMeta {
  const folder = memoryFolders[id];
  if (!folder) {
    throw new Error("Folder not found");
  }
  const updated: SampleFolderMeta = {
    ...folder,
    name: nextName.trim() || "Folder",
  };
  memoryFolders = { ...memoryFolders, [id]: updated };
  persistLibrary();
  notifySampleCatalogChanged();
  return updated;
}

export async function moveSampleToFolder(
  sampleId: string,
  folderId: string | null,
  sortKey?: number,
): Promise<SampleAssetMeta> {
  await readLibrary();
  return moveSampleToFolderSync(sampleId, folderId, sortKey);
}

export function moveSampleToFolderSync(
  sampleId: string,
  folderId: string | null,
  sortKey?: number,
): SampleAssetMeta {
  if (isFactorySampleId(sampleId)) {
    throw new Error("Cannot move a factory sample");
  }
  const meta = memoryIndex[sampleId];
  if (!meta) {
    throw new Error("Sample not found");
  }
  const nextFolder = normalizeFolderId(folderId);
  assertFolderExists(nextFolder);
  const updated: SampleAssetMeta = {
    ...meta,
    folderId: nextFolder,
    sortKey: sortKey ?? nextSortKey(nextFolder, "sample"),
  };
  if (!nextFolder) {
    delete updated.folderId;
  }
  memoryIndex = { ...memoryIndex, [sampleId]: updated };
  persistLibrary();
  notifySampleCatalogChanged();
  return updated;
}

export async function moveSampleFolder(
  folderId: string,
  parentId: string | null,
  sortKey?: number,
): Promise<SampleFolderMeta> {
  await readLibrary();
  return moveSampleFolderSync(folderId, parentId, sortKey);
}

export function moveSampleFolderSync(
  folderId: string,
  parentId: string | null,
  sortKey?: number,
): SampleFolderMeta {
  const folder = memoryFolders[folderId];
  if (!folder) {
    throw new Error("Folder not found");
  }
  const nextParent = normalizeFolderId(parentId);
  if (nextParent === folderId) {
    throw new Error("Cannot move a folder into itself");
  }
  if (nextParent && isFolderAncestor(nextParent, folderId)) {
    throw new Error("Cannot move a folder into one of its descendants");
  }
  assertFolderExists(nextParent);
  const updated: SampleFolderMeta = {
    ...folder,
    parentId: nextParent,
    sortKey: sortKey ?? nextSortKey(nextParent, "folder"),
  };
  memoryFolders = { ...memoryFolders, [folderId]: updated };
  persistLibrary();
  notifySampleCatalogChanged();
  return updated;
}

/**
 * Snapshot a folder subtree (folders + sample PCM) for undoable delete.
 * Does not mutate the catalog.
 */
export async function captureDeletedFolderPayload(folderId: string): Promise<DeletedFolderPayload> {
  await readLibrary();
  if (!memoryFolders[folderId]) {
    throw new Error("Folder not found");
  }
  const subtree = collectFolderSubtreeIds(folderId);
  const subtreeSet = new Set(subtree);
  const samplePayloads: DeletedSamplePayload[] = [];
  for (const sample of Object.values(memoryIndex)) {
    const fid = normalizeFolderId(sample.folderId);
    if (!fid || !subtreeSet.has(fid)) {
      continue;
    }
    const runtime = await resolveSampleRuntime(sample.id);
    if (!runtime) {
      continue;
    }
    const pcm = new Float32Array(runtime.pcm.length);
    pcm.set(runtime.pcm);
    samplePayloads.push({ meta: { ...sample }, pcm });
  }
  const folders = subtree
    .map(id => memoryFolders[id])
    .filter((f): f is SampleFolderMeta => Boolean(f))
    .map(f => ({ ...f }));
  return { folders, samples: samplePayloads };
}

/** Remove folders + samples described by a captured delete payload. */
export function forgetSampleFolderSync(payload: DeletedFolderPayload): void {
  for (const sample of payload.samples) {
    forgetSampleAssetSync(sample.meta.id);
  }
  const nextFolders = { ...memoryFolders };
  for (const folder of payload.folders) {
    delete nextFolders[folder.id];
  }
  memoryFolders = nextFolders;
  persistLibrary();
  notifySampleCatalogChanged();
}

/**
 * Delete a folder and its entire subtree (folders + samples).
 * Capture PCM first for undo. Returns payload suitable for restore.
 */
export async function deleteSampleFolder(folderId: string): Promise<DeletedFolderPayload> {
  const payload = await captureDeletedFolderPayload(folderId);
  forgetSampleFolderSync(payload);
  return payload;
}

/** Restore a deleted folder subtree (folders first, then samples). */
export function restoreSampleFolderSync(payload: DeletedFolderPayload): void {
  const nextFolders = { ...memoryFolders };
  for (const folder of payload.folders) {
    nextFolders[folder.id] = { ...folder };
  }
  memoryFolders = nextFolders;
  persistLibrary();
  for (const sample of payload.samples) {
    restoreSampleAssetSync(sample);
  }
  notifySampleCatalogChanged();
}

async function removeSampleDir(id: string): Promise<void> {
  const dir = await samplesDir();
  if (!dir) {
    return;
  }
  try {
    await dir.removeEntry(id, { recursive: true });
  } catch {
    // already gone
  }
}

/**
 * Permanently clear the entire imported sample library (folders + samples).
 * Factory cycles are untouched. Not undoable.
 */
export async function wipeSampleLibrary(): Promise<{ sampleCount: number; folderCount: number }> {
  await readLibrary();
  const sampleIds = Object.keys(memoryIndex);
  const folderCount = Object.keys(memoryFolders).length;
  const sampleCount = sampleIds.length;

  for (const id of sampleIds) {
    memoryPcm.delete(id);
    removeCachedSample(id);
  }
  memoryIndex = {};
  memoryFolders = {};
  libraryHydrated = true;
  await writeLibrary({
    version: LIBRARY_VERSION,
    folders: {},
    samples: {},
  });

  const dir = await samplesDir();
  if (dir) {
    // `FileSystemDirectoryHandle.values()` is not everywhere; use keys().
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const handle = dir as any;
      if (typeof handle.keys === "function") {
        for await (const name of handle.keys() as AsyncIterable<string>) {
          try {
            await dir.removeEntry(name, { recursive: true });
          } catch {
            // ignore
          }
        }
      } else {
        for (const id of sampleIds) {
          await removeSampleDir(id);
        }
      }
    } catch {
      for (const id of sampleIds) {
        await removeSampleDir(id);
      }
    }
  }

  notifySampleCatalogChanged();
  return { sampleCount, folderCount };
}

/**
 * Delete an imported sample from memory / cache / OPFS.
 * Returns a payload suitable for undo restore. Rejects factory ids.
 */
export async function deleteSampleAsset(id: string): Promise<DeletedSamplePayload> {
  if (isFactorySampleId(id)) {
    throw new Error("Cannot delete a factory sample");
  }
  const runtime = await resolveSampleRuntime(id);
  const index = await readIndex();
  const meta = index[id];
  if (!meta || !runtime) {
    throw new Error("Sample not found");
  }
  const pcm = new Float32Array(runtime.pcm.length);
  pcm.set(runtime.pcm);
  forgetSampleAssetSync(id);
  return { meta, pcm };
}

/**
 * Remove sample from host memory/cache and schedule OPFS cleanup.
 * Prefer capturing PCM via resolveSampleRuntime before calling when undo is needed.
 */
export function forgetSampleAssetSync(id: string): void {
  if (isFactorySampleId(id)) {
    return;
  }
  const index = { ...memoryIndex };
  delete index[id];
  memoryIndex = index;
  memoryPcm.delete(id);
  removeCachedSample(id);
  void writeIndex(index);
  void removeSampleDir(id);
  notifySampleCatalogChanged();
}

/** Restore a deleted sample (undo). */
export async function restoreSampleAsset(payload: DeletedSamplePayload): Promise<SampleAssetMeta> {
  return restoreSampleAssetSync(payload);
}

/** Sync restore into memory/cache; OPFS write is scheduled. */
export function restoreSampleAssetSync(payload: DeletedSamplePayload): SampleAssetMeta {
  const { meta, pcm } = payload;
  if (isFactorySampleId(meta.id)) {
    throw new Error("Cannot restore a factory sample id");
  }
  memoryIndex = { ...memoryIndex, [meta.id]: meta };
  memoryPcm.set(meta.id, pcm);
  putCachedSample({
    id: meta.id,
    name: meta.name,
    sampleRate: meta.sampleRate,
    pcm,
    rootKey: meta.rootKey,
    oneCycle: meta.oneCycle,
  });
  void writeIndex(memoryIndex);
  void writePcm(meta.id, pcm);
  notifySampleCatalogChanged();
  return meta;
}

/** Test helper — clear in-memory catalog (does not wipe OPFS). */
export function resetSampleLibraryMemoryForTests(): void {
  memoryIndex = {};
  memoryFolders = {};
  memoryPcm.clear();
  libraryHydrated = true;
  hydratePromise = null;
}
